/*
 * PREDICTIVE SEARCH
 * Binds to any <predictive-search> custom element. Fetches live results from
 * the Section Rendering API as the user types and injects them into the
 * element's results container.
 *
 * Markup contract (per instance):
 *   <predictive-search>
 *     <form ...><input type="search" name="q"></form>
 *     <div id="predictive-search" | data-results></div>   // results container
 *   </predictive-search>
 *
 * Optional sibling toggled with results (modal only):
 *   [data-search-default]  -> shown when query is empty, hidden when results show
 */
/*
 * Each word in double quotes: samira sand -> "samira" "sand". Shopify answers
 * quoted words by keyword, and bare words with its semantic search as well,
 * which padded "samira" (two products) out to 177 related ones (Jeanne,
 * 2026-10-19). Quoted words keep stemming but lose typo tolerance, so a quoted
 * search that finds nothing is asked again with the bare words. A query with a
 * quote or a colon is search syntax the shopper wrote, and returns null.
 * snippets/search-strict-terms.liquid is the Liquid twin.
 */
function strictSearchTerms(query) {
  if (/[":]/.test(query)) return null;
  const words = query.trim().split(/\s+/).filter(Boolean);
  return words.length ? words.map((word) => `"${word}"`).join(' ') : null;
}

/*
 * Every search bar submits the quoted words, with strict=1 so main-search knows
 * an empty answer may go back to the bare words. Without JS the form submits
 * the bare words, as before.
 */
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('form.searchbar')) return;
  const field = form.elements.q;
  const strict = field && strictSearchTerms(field.value);
  if (!strict) return;
  event.preventDefault();
  const params = new URLSearchParams(new FormData(form));
  params.set('q', strict);
  params.set('strict', '1');
  window.location.assign(`${form.getAttribute('action')}?${params.toString()}`);
});

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.results =
      this.querySelector('[data-results]') ||
      this.querySelector('#predictive-search') ||
      this.querySelector('.search-predictive');

    // Optional default panel (popular searches / quick links) shown when empty.
    // Resolved lazily via getDefaultPanel() because the sibling element may not
    // exist yet when this custom element's constructor runs.
    this.defaultPanel = null;

    if (!this.input || !this.results) return;

    this.abortController = null;

    // A persistent polite status, because the results are replaced while focus
    // stays in the field and a region's name does not announce that (Codex,
    // 2026-10-06). The message comes from the fetched section, localized.
    this.status = this.querySelector('[data-predictive-status]');

    // The debounce timer lives on the element, not inside a closure, so a
    // reset can cancel a search that has not started yet. Escape pressed
    // within 250ms of typing used to dismiss the panel and then watch the
    // pending search open it again (Codex, second pass, 2026-10-01).
    this.debounceTimer = null;
    this.input.addEventListener('input', (event) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        this.onChange(event);
      }, 250);
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.reset();
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.reset();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // Cancel any in-flight request so stale results never overwrite fresh ones.
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.results.setAttribute('aria-busy', 'true');
    const { signal } = this.abortController;

    const strict = strictSearchTerms(searchTerm);
    // The quoted words first; when they find no product, the bare words.
    const request = strict
      ? this.fetchSection(strict, signal).then((section) =>
          section && section.querySelector('#predictive-search-products')
            ? section
            : this.fetchSection(searchTerm, signal))
      : this.fetchSection(searchTerm, signal);

    request
      .then((section) => {
        // The field may have been cleared, or the panel reset, while this was
        // in flight; reset() aborts, and this catches whatever slips past.
        if (this.getQuery() !== searchTerm) return;
        this.results.innerHTML = section ? section.innerHTML : '';
        this.seeAllFor(searchTerm);
        this.results.removeAttribute('aria-busy');
        this.announce();
        this.open();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.results.removeAttribute('aria-busy');
        this.reset();
      });
  }

  /*
   * "See all results" is built in Liquid from predictive_search.terms, which
   * holds the quotes this script added, so the section strips every quote and
   * re-quotes each word: a shopper's "blue dress" came back as "blue" "dress"
   * (Codex review, 2026-10-31). The link is rebuilt here from the words as
   * typed, by the same rule the search bar's submit uses.
   */
  seeAllFor(searchTerm) {
    const more = this.results.querySelector('.predictive__more');
    if (!more) return;
    const strict = strictSearchTerms(searchTerm);
    const params = new URLSearchParams({ q: strict || searchTerm.trim(), type: 'product' });
    if (strict) params.set('strict', '1');
    more.setAttribute('href', `${more.getAttribute('href').split('?')[0]}?${params.toString()}`);
  }

  fetchSection(query, signal) {
    const params = new URLSearchParams({
      q: query,
      // Products only, per the client on 2026-08-25. A popout is a quick pick
      // from the catalogue, and mixing pages and collections into it put three
      // shapes of answer in one short list.
      'resources[type]': 'product',
      // Four: the popout shows one row of four cards, and the rest were rendered,
      // transferred and lazy-loaded for nothing (Codex, 2026-10-06).
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    return fetch(`/search/suggest?${params.toString()}`, {
      signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) =>
        new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search'));
  }

  announce() {
    if (!this.status) return;
    const box = this.results.querySelector('#predictive-search-results');
    this.status.textContent = (box && box.getAttribute('data-status')) || '';
  }

  getDefaultPanel() {
    if (!this.defaultPanel) {
      const scope = this.closest('.search-modal__inner') || this.parentElement || document;
      this.defaultPanel = scope.querySelector('[data-search-default]');
    }
    return this.defaultPanel;
  }

  open() {
    this.results.hidden = false;
    const panel = this.getDefaultPanel();
    if (panel) panel.hidden = true;
  }

  close() {
    this.results.hidden = true;
  }

  // Return to the empty/default state. Cancels the request in flight too: its
  // answer used to arrive after a clear or Escape and reopen the old results
  // (Codex review, 2026-10-01).
  reset() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.results.innerHTML = '';
    this.results.removeAttribute('aria-busy');
    if (this.status) this.status.textContent = '';
    this.close();
    const panel = this.getDefaultPanel();
    if (panel) panel.hidden = false;
  }

  // Removed from the page (a section re-render): nothing pending may land.
  disconnectedCallback() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    if (this.abortController) this.abortController.abort();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        // Keep results if there is still a query (e.g. clicked scrollbar);
        // otherwise fall back to the default panel.
        if (!this.getQuery().length) this.reset();
      }
    }, 200);
  }
}

customElements.define('predictive-search', PredictiveSearch);
